/**
 * Speaker Target Markers Component
 *
 * Renders target point markers and aim lines for wall speakers.
 * - Shared target (linked mode): Single target point for all linked speakers
 * - Individual targets (unlinked mode): Separate target point per speaker
 */

import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { getDrawingBox, uiSlice } from 'reduxModules/ducks/ui';
import { getAreaProperty } from 'reduxModules/selectors/containerSelectors';
import ReducersIndex from 'reduxModules/ducks';
import useMousePosition from 'hooks/useMousePosition';
import { RootState } from 'types/redux/state';
import { AnyAction } from '@reduxjs/toolkit';

interface SpeakerTargetMarkersProps {
  id: string;  // Area ID
  origin: { left: number; top: number };
}

/**
 * SVG crosshair target icon
 */
const TargetIcon: React.FC<{ size: number; color: string }> = ({ size, color }) => (
  <g>
    {/* Outer circle */}
    <circle
      cx={0}
      cy={0}
      r={size}
      fill="none"
      stroke={color}
      strokeWidth={2}
      opacity={0.9}
    />
    {/* Inner circle */}
    <circle
      cx={0}
      cy={0}
      r={size * 0.5}
      fill={color}
      opacity={0.3}
    />
    {/* Crosshair lines */}
    <line
      x1={-size}
      y1={0}
      x2={size}
      y2={0}
      stroke={color}
      strokeWidth={2}
      opacity={0.9}
    />
    <line
      x1={0}
      y1={-size}
      x2={0}
      y2={size}
      stroke={color}
      strokeWidth={2}
      opacity={0.9}
    />
  </g>
);

/**
 * SpeakerTargetMarkers component
 */
const SpeakerTargetMarkers: React.FC<SpeakerTargetMarkersProps> = ({ id, origin }) => {
  const dispatch = useDispatch();
  const { bbox, scale, zoom } = useSelector(getDrawingBox);
  const mouse = useMousePosition();

  // Drag state
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragTarget, setDragTarget] = useState<{ type: 'shared' | 'individual'; speakerIndex?: number } | null>(null);

  // Store temporary target position during drag (only update Redux on mouse release)
  const [tempTargetPosition, setTempTargetPosition] = useState<{ x: number; y: number } | null>(null);
  const [tempIndividualTargets, setTempIndividualTargets] = useState<Map<number, { x: number; y: number }>>(new Map());

  // Throttle state using requestAnimationFrame for smooth updates
  const rafRef = useRef<number | null>(null);
  const pendingUpdateRef = useRef<{ targetPoint: { x: number; y: number }; type: 'shared' | 'individual'; speakerId?: string } | null>(null);

  // Get speaker configuration
  const speakers = useSelector((state: RootState) =>
    getAreaProperty(state, 'speakers')
  );

  // Get microphone configuration to check if bar is selected
  const microphones = useSelector((state: RootState) =>
    getAreaProperty(state, 'microphones')
  );

  const deviceIsMoving = useSelector((state: RootState) =>
    state.ui?.deviceIsMoving
  );

  // Get current view to determine if markers should be shown
  const currentView = useSelector((state: RootState) =>
    state.ui?.currentView
  );

  // Get room dimensions for boundary constraints
  const roomDimensions = useSelector((state: RootState) => {
    const area = state.container?.areas?.[id];
    return {
      length: area?.dimensions?.length || 6.096,
      width: area?.dimensions?.width || 4.2672,
      left: area?.location?.left || 0,
      top: area?.location?.top || 0
    };
  }, shallowEqual);

  const x0 = bbox.x0 + origin.left * scale * (!zoom ? 1 : 0);
  const y0 = bbox.y0 + origin.top * scale * (!zoom ? 1 : 0);

  // Force re-renders during drag to update aim lines
  const [, forceUpdate] = useState({});
  useEffect(() => {
    if (deviceIsMoving) {
      const intervalId = setInterval(() => {
        forceUpdate({});
      }, 16); // ~60fps
      return () => clearInterval(intervalId);
    }
  }, [deviceIsMoving]);

  // Cleanup pending RAF on unmount or when dragging stops
  useEffect(() => {
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      // Ensure aimpoint dragging flag is reset on unmount
      dispatch(uiSlice.actions.setAimpointDragging(false));
    };
  }, [dispatch]);

  useEffect(() => {
    if (!dragging && rafRef.current !== null) {
      // Cancel any pending RAF when drag ends
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      pendingUpdateRef.current = null;
      // Clear temporary positions when drag ends
      setTempTargetPosition(null);
      setTempIndividualTargets(new Map());
      // Ensure Redux flag is cleared
      dispatch(uiSlice.actions.setAimpointDragging(false));
    }
  }, [dragging, dispatch]);

  /**
   * Handle pointer down on target marker
   */
  const onPointerDown = (e: React.PointerEvent, type: 'shared' | 'individual', speakerIndex?: number): void => {
    const target = e.currentTarget as SVGElement;
    if (!target) return;

    // Set pointer capture
    target.setPointerCapture(e.pointerId);

    // Calculate offset from mouse to marker center
    const { left, right, top, bottom } = target.getBoundingClientRect();
    setOffset({
      x: e.clientX - (right + left) / 2,
      y: e.clientY - (top + bottom) / 2
    });

    // Set drag state
    setDragging(true);
    setDragTarget({ type, speakerIndex });

    // Notify Redux that aimpoint dragging has started (hide ruler)
    dispatch(uiSlice.actions.setAimpointDragging(true));

    e.stopPropagation();
  };

  /**
   * Handle pointer move - update target position visually only (no Redux dispatch until mouse release)
   * Uses requestAnimationFrame to throttle visual updates for smooth performance
   */
  const onPointerMove = (e: React.PointerEvent): void => {
    if (!dragging || !dragTarget || !mouse) return;

    const { x = 0, y = 0 } = mouse;
    if (!x || !y) return;

    // Convert screen coordinates to room coordinates
    let roomX = (x - x0) / scale;
    let roomY = (y - y0) / scale;

    // Constrain target to room boundaries
    const { left: roomLeft, top: roomTop, length: roomLength, width: roomWidth } = roomDimensions;
    roomX = Math.max(roomLeft, Math.min(roomLeft + roomLength, roomX));
    roomY = Math.max(roomTop, Math.min(roomTop + roomWidth, roomY));

    const targetPoint = { x: roomX, y: roomY };

    // Store pending update for visual rendering only
    if (dragTarget.type === 'shared') {
      pendingUpdateRef.current = { targetPoint, type: 'shared' };
    } else if (dragTarget.type === 'individual' && dragTarget.speakerIndex !== undefined) {
      const speakerId = `wall-speaker-${dragTarget.speakerIndex + 1}`;
      pendingUpdateRef.current = { targetPoint, type: 'individual', speakerId };
    }

    // Schedule visual update with requestAnimationFrame for smooth 60fps rendering
    // NO REDUX DISPATCH - only update local state for visual feedback
    if (rafRef.current === null) {
      rafRef.current = requestAnimationFrame(() => {
        if (pendingUpdateRef.current) {
          const { targetPoint, type } = pendingUpdateRef.current;

          if (type === 'shared') {
            setTempTargetPosition(targetPoint);
          } else if (type === 'individual' && dragTarget.speakerIndex !== undefined) {
            setTempIndividualTargets(prev => new Map(prev).set(dragTarget.speakerIndex!, targetPoint));
          }

          pendingUpdateRef.current = null;
        }
        rafRef.current = null;
      });
    }
  };

  /**
   * Handle pointer up - finalize drag and dispatch Redux action for plot recalculation
   */
  const onPointerUp = (e: React.PointerEvent): void => {
    if (!dragging) return;

    const target = e.currentTarget as SVGElement;
    if (target) {
      target.releasePointerCapture(e.pointerId);
    }

    // Dispatch final position update to Redux (this triggers plot recalculation)
    // Use temp position if available, otherwise use pending update
    if (dragTarget) {
      let finalTargetPoint: { x: number; y: number } | null = null;
      let speakerId: string | undefined = undefined;

      if (dragTarget.type === 'shared' && tempTargetPosition) {
        finalTargetPoint = tempTargetPosition;
      } else if (dragTarget.type === 'individual' && dragTarget.speakerIndex !== undefined) {
        finalTargetPoint = tempIndividualTargets.get(dragTarget.speakerIndex) || null;
        speakerId = `wall-speaker-${dragTarget.speakerIndex + 1}`;
      } else if (pendingUpdateRef.current) {
        // Fallback to pending update if temp state not available
        finalTargetPoint = pendingUpdateRef.current.targetPoint;
        speakerId = pendingUpdateRef.current.speakerId;
      }

      // Dispatch the final position to Redux (this will trigger plot recalculation)
      if (finalTargetPoint) {
        if (dragTarget.type === 'shared') {
          dispatch(
            (ReducersIndex.container as any).updateSpeakerTarget({ targetPoint: finalTargetPoint }) as unknown as AnyAction
          );
        } else if (dragTarget.type === 'individual' && speakerId) {
          dispatch(
            (ReducersIndex.container as any).updateSpeakerTarget({ targetPoint: finalTargetPoint, speakerId }) as unknown as AnyAction
          );
        }
      }
    }

    // Cancel any pending RAF
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    // Clear drag state and temp positions
    pendingUpdateRef.current = null;
    setDragging(false);
    setDragTarget(null);
    setTempTargetPosition(null);
    setTempIndividualTargets(new Map());

    // Notify Redux that aimpoint dragging has ended (show ruler)
    dispatch(uiSlice.actions.setAimpointDragging(false));

    e.stopPropagation();
  };

  // Only render for wall speakers in the speakers or acoustics view
  if (!speakers || speakers.mountingType !== 'wall') {
    return null;
  }

  // Hide target markers and lines when bar microphone is selected
  // Bar microphones (VBC 2500a, VBC 2800, ABC 2500a) don't use wall speaker targeting
  if (microphones && microphones.model === 'bar') {
    return null;
  }

  // Hide target markers and lines when not in speakers or acoustics view
  if (!currentView || !['speakers', 'acoustics'].includes(currentView)) {
    return null;
  }

  const { linkedSpeakers, targetPoint, layout } = speakers;
  const markerSize = 8; // Base size in SVG units

  /**
   * Get the actual visual position of a speaker element (accounts for drag transforms)
   */
  const getSpeakerPosition = (speakerId: string, defaultX: number, defaultY: number): { x: number, y: number } => {
    const element = document.getElementById(speakerId);
    if (element) {
      const transform = element.getAttribute('transform') || '';
      const translateMatch = transform.match(/translate\(([-\d.]+)[,\s]+([-\d.]+)\)/);
      if (translateMatch) {
        return {
          x: parseFloat(translateMatch[1]),
          y: parseFloat(translateMatch[2])
        };
      }
    }
    return { x: defaultX, y: defaultY };
  };

  // Render shared target (linked mode only)
  if (linkedSpeakers && targetPoint) {
    // Use temporary position during drag, otherwise use Redux state
    const currentTarget = tempTargetPosition || targetPoint;

    return (
      <g className="speaker-target-markers">
        {/* Aim lines from each speaker to shared target */}
        {layout?.map((speaker: any, index: number) => {
          const defaultX = x0 + speaker.location.x * scale;
          const defaultY = y0 + speaker.location.y * scale;

          // Get actual visual position (handles drag transforms)
          const speakerId = `${id}-speakers-${index}`;
          const { x: speakerX, y: speakerY } = getSpeakerPosition(speakerId, defaultX, defaultY);

          const targetX = x0 + currentTarget.x * scale;
          const targetY = y0 + currentTarget.y * scale;

          return (
            <line
              key={`aim-line-${index}`}
              x1={speakerX}
              y1={speakerY}
              x2={targetX}
              y2={targetY}
              stroke="#4CAF50"
              strokeWidth={1.5}
              strokeDasharray="4 2"
              opacity={0.6}
              pointerEvents="none"
            />
          );
        })}

        {/* Shared target marker */}
        <g
          transform={`translate(${x0 + currentTarget.x * scale}, ${y0 + currentTarget.y * scale})`}
          style={{ cursor: dragging ? 'grabbing' : 'move' }}
          data-target-type="shared"
          onPointerDown={(e) => onPointerDown(e, 'shared')}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
        >
          <TargetIcon size={markerSize} color="#F44336" />
        </g>
      </g>
    );
  }

  // Render individual targets (unlinked mode)
  if (!linkedSpeakers && layout) {
    return (
      <g className="speaker-target-markers">
        {layout.map((speaker: any, index: number) => {
          const speakerTarget = speaker.targetPoint;

          // Skip if this speaker has no target
          if (!speakerTarget) {
            return null;
          }

          // Use temporary position during drag, otherwise use Redux state
          const currentTarget = tempIndividualTargets.get(index) || speakerTarget;

          const defaultX = x0 + speaker.location.x * scale;
          const defaultY = y0 + speaker.location.y * scale;

          // Get actual visual position (handles drag transforms)
          const speakerId = `${id}-speakers-${index}`;
          const { x: speakerX, y: speakerY } = getSpeakerPosition(speakerId, defaultX, defaultY);

          const targetX = x0 + currentTarget.x * scale;
          const targetY = y0 + currentTarget.y * scale;

          return (
            <g key={`individual-target-${index}`}>
              {/* Aim line from speaker to individual target */}
              <line
                x1={speakerX}
                y1={speakerY}
                x2={targetX}
                y2={targetY}
                stroke="#4CAF50"
                strokeWidth={1.5}
                strokeDasharray="4 2"
                opacity={0.6}
                pointerEvents="none"
              />

              {/* Individual target marker */}
              <g
                transform={`translate(${targetX}, ${targetY})`}
                style={{ cursor: dragging ? 'grabbing' : 'move' }}
                data-target-type="individual"
                data-speaker-index={index}
                onPointerDown={(e) => onPointerDown(e, 'individual', index)}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
              >
                <TargetIcon size={markerSize} color="#F44336" />
              </g>
            </g>
          );
        })}
      </g>
    );
  }

  return null;
};

export default SpeakerTargetMarkers;